<div class="textarea-box" [class.card-frame]="isCard">
  <!-- Optional toolbar: label chip + select + action buttons (mockup card header) -->
  <div class="textarea-toolbar" *ngIf="toolbar && !savedMode && !resultMode">
    <div class="toolbar-picker">
      <span class="toolbar-label" *ngIf="toolbar.label">{{ toolbar.label }}</span>
      <div class="toolbar-select-wrap" *ngIf="toolbar.select">
        <ng-select
          class="toolbar-select"
          [items]="filteredToolbarOptions"
          bindLabel="label"
          bindValue="value"
          [multiple]="toolbar.select.isMultiSelect === true"
          [searchable]="toolbar.select.isMultiSelect !== true && toolbar.select.searchable === true"
          [placeholder]="toolbar.select.placeholder || ''"
          [clearable]="false"
          [closeOnSelect]="toolbar.select.isMultiSelect !== true"
          appendTo="body"
          [ngModel]="toolbarValue"
          (ngModelChange)="onToolbarValueChange($event)"
          (open)="onToolbarDropdownOpen()"
        >
          <ng-template ng-header-tmp *ngIf="toolbar.select.isMultiSelect === true">
            <div class="toolbar-multi-header">
              <input
                #toolbarSearchInput
                type="text"
                class="toolbar-multi-search"
                placeholder="Search"
                [ngModel]="toolbarSearchText"
                (ngModelChange)="onToolbarSearchChange($event)"
                (click)="$event.stopPropagation()"
                (keydown)="$event.stopPropagation()"
              />
              <label class="toolbar-select-all">
                <input
                  type="checkbox"
                  [checked]="areAllToolbarOptionsSelected"
                  (change)="toggleAllToolbarOptions($event)"
                />
                <span>Select All</span>
              </label>
            </div>
          </ng-template>

          <ng-template ng-multi-label-tmp let-items="items">
            <span
              class="toolbar-multi-label"
              [title]="toolbarSelectionTitle(items)"
            >{{ toolbarSelectionText(items) }}</span>
          </ng-template>

          <ng-template ng-option-tmp let-item="item">
            <div class="toolbar-checkbox-option" [title]="item.label">
              <input
                *ngIf="toolbar.select.isMultiSelect === true"
                type="checkbox"
                tabindex="-1"
                [checked]="isToolbarOptionSelected(item)"
              />
              <span>{{ item.label }}</span>
            </div>
          </ng-template>
        </ng-select>
        <span class="toolbar-caret" aria-hidden="true"></span>
      </div>
    </div>
    <div class="toolbar-actions">
      <button
        type="button"
        class="textarea-btn"
        *ngFor="let btn of toolbarButtons"
        [class.textarea-btn-primary]="(btn.style ?? 'primary') === 'primary'"
        [class.textarea-btn-secondary]="btn.style === 'secondary'"
        [class.textarea-btn-danger]="btn.style === 'danger'"
        (click)="onToolbarButton(btn)"
      >
        {{ btn.label }}
      </button>
      <button
        type="button"
        class="textarea-btn textarea-btn-secondary"
        *ngIf="showCancelColumnEdit"
        [disabled]="savedSaving"
        (click)="cancelColumnSelectionEdit()"
      >
        Cancel Edit
      </button>
    </div>
  </div>

  <!-- Saved-selection picker replaces the field inside this same card. -->
  <ng-container *ngIf="savedMode" [ngTemplateOutlet]="savedTemplate"></ng-container>

  <!-- Loading a saved selection swaps in the configured result widget, still
       inside this same card. When a `preRequest` is configured the widget waits
       for that response so it can be sent along in the widget's own payload. -->
  <div class="result-view" *ngIf="resultMode && resultView">
    <p class="saved-panel-note" *ngIf="resultLoading">{{ resultLoadingMessage }}</p>
    <p class="saved-panel-note saved-panel-error" *ngIf="!resultLoading && resultError">{{ resultError }}</p>
    <app-json-driven-smart-table
      *ngIf="!resultLoading && resolvedResultView && isSmartTableResult"
      [data]="$any(resolvedResultView)"
    ></app-json-driven-smart-table>
  </div>

  <!-- Inline mode renders the field straight into the grid cell. Dialog mode
       keeps the cell empty and waits for `openOnKey` to fire. -->
  <ng-container
    *ngIf="!savedMode && !resultMode && !isDialogMode"
    [ngTemplateOutlet]="fieldTemplate"
  ></ng-container>
  <span
    class="textarea-error toolbar-action-error"
    *ngIf="toolbarActionError && !savedMode && !resultMode"
  >
    {{ toolbarActionError }}
  </span>
</div>

<ng-template #savedTemplate>
  <div class="saved-panels" [style.height]="fieldFixedHeight">
    <div class="saved-panel saved-panel-list">
      <h3 class="saved-panel-title">{{ savedListTitle }}</h3>
      <div class="saved-panel-body">
        <p class="saved-panel-note" *ngIf="savedLoading">{{ savedLoadingMessage }}</p>
        <p class="saved-panel-note saved-panel-error" *ngIf="savedError">{{ savedError }}</p>
        <p class="saved-panel-note" *ngIf="!savedLoading && !savedError && !savedItems.length">
          {{ savedEmptyMessage }}
        </p>

        <ol class="saved-list" *ngIf="savedItems.length">
          <li
            class="saved-list-item"
            *ngFor="let item of savedItems"
            [class.is-active]="isActiveSavedItem(item)"
            (click)="selectSavedItem(item)"
          >
            {{ item.name }}
          </li>
        </ol>
      </div>
    </div>

    <div class="saved-panel saved-panel-columns">
      <h3 class="saved-panel-title">{{ savedPanelTitle }}</h3>
      <div class="saved-panel-body">
        <p class="saved-panel-note" *ngIf="savedSaving">{{ savingMessage }}</p>
        <p class="saved-panel-note saved-panel-error" *ngIf="savedEditError">{{ savedEditError }}</p>
        <ul class="saved-columns" [class.is-editing]="savedEditMode">
          <li *ngFor="let column of activeSavedColumns">
            <button
              type="button"
              class="saved-column-remove"
              *ngIf="savedEditMode"
              [disabled]="savedSaving"
              [attr.aria-label]="'Remove ' + column"
              title="Remove column"
              (click)="removeSavedColumn(column)"
            >&minus;</button>
            <span class="saved-column-name">{{ columnDisplayLabel(column) }}</span>
          </li>
        </ul>
      </div>

      <div class="saved-actions">
        <button
          type="button"
          class="textarea-btn"
          *ngFor="let btn of savedButtons"
          [class.textarea-btn-primary]="btn.style === 'primary'"
          [class.textarea-btn-secondary]="(btn.style ?? 'secondary') === 'secondary'"
          [disabled]="isSavedButtonDisabled(btn)"
          (click)="onSavedButton(btn)"
        >
          {{ btn.label }}
        </button>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #fieldTemplate>
  <label class="textarea-label" *ngIf="labelText">
    {{ labelText }}<span class="required-mark" *ngIf="isRequired">*</span>
  </label>

  <div
    class="column-selection-list"
    [class.is-invalid]="showError"
    [style.height]="fieldFixedHeight"
    *ngIf="data.removableSelectionList === true; else textareaControl"
  >
    <ul>
      <li *ngFor="let column of columnSelectionValues">
        <button
          type="button"
          class="saved-column-remove"
          *ngIf="showCancelColumnEdit"
          [attr.aria-label]="'Remove ' + column"
          title="Remove column"
          (click)="removeColumnSelection(column)"
        >&minus;</button>
        <span>{{ columnDisplayLabel(column) }}</span>
      </li>
    </ul>
    <p class="saved-panel-note" *ngIf="savedSaving">{{ savingMessage }}</p>
    <p class="saved-panel-note saved-panel-error" *ngIf="savedEditError">{{ savedEditError }}</p>
  </div>

  <ng-template #textareaControl>
    <textarea
      class="textarea-control"
      [class.is-invalid]="showError"
      [formControl]="control"
      [rows]="rows"
      [readOnly]="isReadonly"
      [attr.placeholder]="placeholder || null"
      [attr.maxlength]="maxLength"
      [style.height]="fieldFixedHeight"
    ></textarea>
  </ng-template>

  <div class="textarea-footer" *ngIf="showError || showCharCount">
    <span class="textarea-error" *ngIf="showError">{{ errorText }}</span>
    <span class="textarea-count" *ngIf="showCharCount">
      {{ charCount }}<ng-container *ngIf="maxLength">/{{ maxLength }}</ng-container>
    </span>
  </div>
</ng-template>

<ng-template #dialogTemplate>
  <div class="textarea-dialog">
    <h2 class="textarea-dialog-title" *ngIf="dialogTitle">{{ dialogTitle }}</h2>

    <div class="textarea-dialog-body">
      <ng-container [ngTemplateOutlet]="fieldTemplate"></ng-container>
    </div>

    <div class="textarea-dialog-actions">
      <button type="button" class="textarea-btn textarea-btn-secondary" (click)="cancelDialog()">
        {{ cancelLabel }}
      </button>
      <button
        type="button"
        class="textarea-btn textarea-btn-primary"
        [disabled]="control.invalid"
        (click)="confirmDialog()"
      >
        {{ confirmLabel }}
      </button>
    </div>
  </div>
</ng-template>
